import { SDKPageData } from "../_types";

export const JSON_SDK_MAJIK_ENVELOPE: SDKPageData = {
  id: "majik-envelope",
  timestamp: new Date("2026-02-11").toISOString(),
  lastUpdate: new Date("2026-02-19").toISOString(),
  metadata: {
    sdkName: "@majikah/majik-envelope",
    sdkSlug: "majik-envelope",
    tagline: "Post-quantum envelope encryption for Majik Message",
    description:
      "The post-quantum cryptographic engine for Majik Message. Implements ML-KEM-768 (NIST FIPS-203) envelope encryption with AES-256-GCM, multi-recipient group support, transparent Zstd/gzip compression, and a compact binary wire format with a human-scannable prefix.",
    seo: "majik-envelope post-quantum ML-KEM-768 AES-256-GCM envelope encryption multi-recipient group messaging JavaScript TypeScript",
    keywords: [
      "post-quantum",
      "ml-kem-768",
      "kyber",
      "aes-256-gcm",
      "envelope-encryption",
      "multi-recipient",
      "group-messaging",
      "e2ee",
      "cryptography",
      "majikah",
      "majik-message",
      "compression",
      "zstd",
      "fips-203",
    ],
    category: "Cryptography",
    subcategory: "Envelope Encryption",
    language: "TypeScript",
    platforms: [
      "Node.js",
      "Browser",
      "Electron",
      "React Native",
      "Cloudflare Workers",
    ],
    photo: {
      logo: "https://storage.majikah.solutions/public/Logo_MajikMessage_Majikah_Thumbnail_WEBP.webp?url",
      banner:
        "https://storage.majikah.solutions/public/MajikMessage_SmallTile.jpg?url",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikMessage_SocialCard.webp?url",
    },
    badges: [
      {
        label: "Developed by",
        value: "Zelijah",
        type: "developed-by",
        url: "https://github.com/jedlsf",
      },
      { label: "Version", value: "2.0.0", type: "version" },
      { label: "Downloads", value: "", type: "downloads" },
      { label: "Bundle Size", value: "", type: "bundle-size" },
      {
        label: "License",
        value: "Apache-2.0",
        type: "license",
        url: "https://opensource.org/licenses/Apache-2.0",
      },
      { label: "TypeScript", value: "Ready", type: "typescript" },
      { label: "Stars", value: "", type: "stars" },
      { label: "Language", value: "TypeScript", type: "language" },
      { label: "", value: "jedlsf", type: "sponsors" },
      { label: "Security", value: "ML KEM 768", type: "custom" },
    ],
  },
  repository: {
    url: "https://github.com/Majikah/majik-envelope",
    owner: "Majikah",
    repo: "majik-envelope",
    branch: "main",
    license: "Apache-2.0",
  },
  hero: {
    title: "Majik Envelope",
    subtitle: "Post-Quantum Envelope Encryption",
    description:
      "Encrypt a message for one recipient or an entire group in a single call. MajikEnvelope uses ML-KEM-768 (NIST FIPS-203) for key encapsulation and AES-256-GCM for symmetric encryption — no classical Diffie-Hellman, no legacy paths. Payloads are transparently compressed and packed into a compact binary format with a human-scannable `~*$MJKMSG:` prefix.",
    codeSnippet: {
      language: "typescript",
      code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// ── Build recipient from a stored MajikKey JSON ──────────────────────────────\nconst aliceKey = MajikKey.fromJSON(storedAliceJson); // locked on load\n\nconst recipient = {\n  fingerprint:    aliceKey.fingerprint,      // base64 SHA-256 of X25519 pubkey\n  mlKemPublicKey: aliceKey.mlKemPublicKey    // Uint8Array, 1184 bytes — no unlock needed\n};\n\n// ── Encrypt ──────────────────────────────────────────────────────────────────\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Hello, post-quantum world.',\n  recipients: [recipient]\n});\n\nconst wire = envelope.toScannerString(); // '~*$MJKMSG:<base64>'\n\n// ── Decrypt (Alice's side) ───────────────────────────────────────────────────\nawait aliceKey.unlock('alice-passphrase');        // Argon2id KDF — ~600–900 ms\nconst plaintext = await envelope.decrypt({\n  fingerprint:    aliceKey.fingerprint,\n  mlKemSecretKey: aliceKey.getMlKemSecretKey()    // Uint8Array, 2400 bytes\n});\naliceKey.lock();                                  // clear private key from memory\n\nconsole.log(plaintext); // 'Hello, post-quantum world.'",
      caption:
        "Public keys need no unlock. Private keys require unlock() — then lock() immediately after.",
    },
    ctaButtons: [
      {
        label: "Install via npm",
        variant: "primary",
        url: "https://www.npmjs.com/package/@majikah/majik-envelope",
        external: true,
      },
      {
        label: "View on GitHub",
        variant: "secondary",
        url: "https://github.com/Majikah/majik-envelope",
        external: true,
      },
    ],
  },
  overview: {
    title: "What is Majik Envelope?",
    paragraphs: [
      "Majik Envelope is the cryptographic message layer of the Majikah ecosystem. It takes plaintext and a list of recipients — identified by their ML-KEM-768 public keys — and produces a self-contained, post-quantum encrypted envelope that only the intended recipients can open.",
      "Single-recipient encryption uses ML-KEM-768 encapsulation directly: the 32-byte shared secret becomes the AES-256-GCM key. Group encryption generates a random group AES key, encrypts the message once, then XOR-wraps that key separately for each recipient using their own ML-KEM shared secret. One ciphertext, N per-recipient key entries.",
      "Envelopes are serialized to a compact binary format — version byte, 32-byte fingerprint, JSON payload — then base64-encoded inside a scannable `~*$MJKMSG:` prefix string. Payloads are transparently compressed with gzip (plaintext mode) or Zstd (binary mode) before encryption, and decompressed automatically on decrypt.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "ML-KEM-768 Only",
        description:
          "v3 envelopes exclusively. No X25519, no legacy paths. Every message is post-quantum secure against future quantum adversaries.",
      },
      {
        icon: "users",
        label: "Single & Group in One API",
        description:
          "Pass one recipient for a solo message, two or more for a group. The API is identical — single vs group is determined automatically from recipients.length.",
      },
      {
        icon: "compress",
        label: "Transparent Compression",
        description:
          "Plaintext is compressed with gzip (via fflate) before encryption. Decompression is automatic on decrypt. Binary payloads use Zstd via WASM for higher ratios.",
      },
      {
        icon: "scan",
        label: "Scannable Wire Format",
        description:
          "Envelopes serialize to a `~*$MJKMSG:<base64>` scanner string — a compact, recognizable prefix that message parsers can identify without decoding the payload.",
      },
    ],
    whatItDoes: [
      "Encrypts plaintext for one or multiple ML-KEM-768 recipients",
      "Derives per-message ephemeral shared secrets via ML-KEM encapsulation",
      "Wraps group AES keys per-recipient using a 32-byte XOR one-time-pad over the ML-KEM shared secret",
      "Compresses plaintext before encryption (gzip for strings, Zstd for binary)",
      "Serializes envelopes to a binary format and a scannable `~*$MJKMSG:` string",
      "Parses envelopes from scanner strings, binary blobs, or stored JSON",
      "Decrypts envelopes given the recipient's ML-KEM-768 secret key",
      "Decompresses payload transparently after decryption",
    ],
    whatItDoesNot: [
      "Does not manage identities or keypairs — use @majikah/majik-key for that",
      "Does not handle key storage, contact management, or conversation state",
      "Does not communicate with any server or relay",
      "Does not support v1 (X25519 solo) or v2 (X25519 group) envelopes — v3 ML-KEM only",
      "Does not sign messages — authentication is handled at the messaging layer",
    ],
    targetUsers: [
      {
        title: "Majikah Messaging Stack Developers",
        description:
          "The encryption engine used internally by @majikah/majik-message. Integrate directly when building custom message transports or relay backends.",
        icon: "message",
      },
      {
        title: "Post-Quantum App Builders",
        description:
          "Need quantum-resistant message encryption today without rolling your own ML-KEM integration? Drop in MajikEnvelope and get a battle-tested, typed implementation.",
        icon: "shield",
      },
      {
        title: "Protocol Implementors",
        description:
          "The binary wire format and scanner string prefix are designed for easy detection and parsing in custom message pipelines, QR code workflows, and data channels.",
        icon: "code",
      },
    ],
  },
  installation: {
    title: "Installation",
    packageManagers: [
      {
        name: "npm",
        command: "npm install @majikah/majik-envelope @majikah/majik-key",
      },
      {
        name: "yarn",

        command: "yarn add @majikah/majik-envelope @majikah/majik-key",
      },
      {
        name: "pnpm",

        command: "pnpm add @majikah/majik-envelope @majikah/majik-key",
      },
      {
        name: "bun",

        command: "bun add @majikah/majik-envelope @majikah/majik-key",
      },
    ],
    verification: [
      {
        description:
          "Verify the install by encrypting and decrypting a test message:",
        code: "import { MajikEnvelope } from '@majikah/majik-envelope';\nimport { generateMlKemKeypair } from '@majikah/majik-envelope/crypto';\n\nconst { publicKey, secretKey } = generateMlKemKeypair();\nconst env = await MajikEnvelope.encrypt({\n  plaintext: 'smoke test',\n  recipients: [{ fingerprint: 'test', mlKemPublicKey: publicKey }]\n});\nconsole.log(await env.decrypt({ fingerprint: 'test', mlKemSecretKey: secretKey }));\n// 'smoke test'",
        expectedOutput: "smoke test",
      },
    ],
  },
  quickStart: {
    title: "Quick Start",
    description:
      "The three core workflows: single-recipient encryption, group encryption, and round-tripping through the wire format.",
    steps: [
      {
        title: "1. Encrypt for a single recipient",
        description:
          "Pass one `MajikRecipient` to `MajikEnvelope.encrypt()`. Provide the recipient's ML-KEM-768 public key (1184 bytes) and their fingerprint (base64 SHA-256 of their X25519 public key, used as an envelope identifier).",
        code: {
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// Load recipient key from storage — stays locked, no passphrase needed\nconst aliceKey = MajikKey.fromJSON(storedAliceJson);\n\n// Public fields are available without unlocking\nconst recipient = {\n  fingerprint:    aliceKey.fingerprint,    // base64 SHA-256 of X25519 pubkey\n  mlKemPublicKey: aliceKey.mlKemPublicKey  // Uint8Array, 1184 bytes\n};\n\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Secret message for Alice.',\n  recipients: [recipient]\n});\n\nconsole.log(envelope.isSingle); // true\nconsole.log(envelope.isGroup);  // false\nconsole.log(envelope.version);  // 3",
        },
        notes: [
          "fingerprint and mlKemPublicKey are public — available on a locked MajikKey with no passphrase.",
          "MajikKey.fromJSON() always returns a locked key. Only call unlock() when you need to decrypt.",
          "Compression is on by default (compress: true). Pass compress: false to skip.",
        ],
      },
      {
        title: "2. Serialize and transmit",
        description:
          "Convert to a scanner string for transport over any text channel — chat, QR code, WebSocket, HTTP body. The `~*$MJKMSG:` prefix makes envelopes easy to detect and parse in a message stream.",
        code: {
          language: "typescript",
          code: "// Scanner string — safe for any text transport\nconst wire = envelope.toScannerString();\n// '~*$MJKMSG:AwAAAB...base64...'\n\n// Binary blob — for binary transports or storage\nconst binary = envelope.toBinary(); // ArrayBuffer\n\n// JSON — for IndexedDB, Supabase, envelope caches\nconst json = envelope.toJSON();\n// { version: 3, fingerprint: string, payload: {...}, plaintext?: string }",
        },
      },
      {
        title: "3. Parse and decrypt",
        description:
          "Parse the wire format back to a `MajikEnvelope`, then call `decrypt()` with the recipient's ML-KEM-768 secret key. Decompression happens automatically.",
        code: {
          language: "typescript",
          code: "// Parse from scanner string\nconst received = MajikEnvelope.fromScannerString(wire);\n\n// Unlock the key — Argon2id derivation, ~600–900 ms. Show a loading indicator.\nawait aliceKey.unlock('alice-passphrase');\n\ntry {\n  const plaintext = await received.decrypt({\n    fingerprint:    aliceKey.fingerprint,\n    mlKemSecretKey: aliceKey.getMlKemSecretKey() // Uint8Array, 2400 bytes\n  });\n  console.log(plaintext); // 'Secret message for Alice.'\n} finally {\n  aliceKey.lock(); // always lock in finally — clear private key even if decrypt() throws\n}",
        },
        notes: [
          "unlock() runs Argon2id (64 MB / 3t / 4p) — always async, always slow by design. Show a spinner.",
          "Wrap decrypt() in try/finally and call lock() in the finally block — this guarantees private key bytes are cleared even on failure.",
          "fingerprint does not require unlock — only getMlKemSecretKey() does.",
        ],
      },
      {
        title: "4. Encrypt for a group",
        description:
          "Pass two or more recipients and a `senderFingerprint`. The message is encrypted once; each recipient gets their own ML-KEM key entry in the payload.",
        code: {
          language: "typescript",
          code: "const envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Hello everyone.',\n  senderFingerprint: myKey.fingerprint,  // stored as envelope fingerprint\n  recipients: [\n    { fingerprint: aliceKey.fingerprint, mlKemPublicKey: aliceKey.mlKemPublicKey },\n    { fingerprint: bobKey.fingerprint,   mlKemPublicKey: bobKey.mlKemPublicKey },\n    { fingerprint: myKey.fingerprint,    mlKemPublicKey: myKey.mlKemPublicKey }\n  ]\n});\n\nconsole.log(envelope.isGroup); // true\n\n// Any recipient can decrypt with their own secret key\nconst text = await envelope.decrypt({\n  fingerprint: bobKey.fingerprint,\n  mlKemSecretKey: bobKey.getMlKemSecretKey()\n});\nconsole.log(text); // 'Hello everyone.'",
        },
        notes: [
          "senderFingerprint is required when recipients.length > 1 — it is stored in the envelope binary header.",
          "Each recipient's decryption is completely independent — they only need their own secret key.",
          "The group AES key is recovered via XOR: aesKey = encryptedAesKey XOR mlKemSharedSecret.",
        ],
      },
    ],
  },
  features: [
    {
      id: "single-encryption",
      title: "Single-Recipient Encryption",
      description:
        "For 1:1 messages, the ML-KEM-768 shared secret is used directly as the AES-256-GCM key — no intermediate key wrapping needed.",
      icon: "lock",
      content: [
        "Call `mlKemEncapsulate(recipientPublicKey)` → produces `{ sharedSecret: 32 bytes, cipherText: 1088 bytes }`.",
        "The 32-byte `sharedSecret` is used directly as the AES-256-GCM encryption key.",
        "A fresh 12-byte random IV is generated for every message.",
        "The payload stores `{ iv, ciphertext, mlKemCipherText }` — the mlKemCipherText is 1088 bytes (ML-KEM-768 ciphertext, base64 encoded).",
        "The envelope fingerprint header is set to the recipient's fingerprint for single messages.",
        "On decryption: `mlKemDecapsulate(mlKemCipherText, secretKey)` → recover `sharedSecret` → AES-GCM decrypt.",
      ],
      codeExample: {
        title: "Single-recipient round-trip",
        language: "typescript",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\nconst aliceKey = MajikKey.fromJSON(storedAliceJson); // locked\n\n// Encrypt — public key only, no unlock\nconst env = await MajikEnvelope.encrypt({\n  plaintext: 'Direct message.',\n  recipients: [{\n    fingerprint:    aliceKey.fingerprint,\n    mlKemPublicKey: aliceKey.mlKemPublicKey\n  }]\n});\n\nconsole.log(env.isSingle);    // true\nconsole.log(env.fingerprint); // aliceKey.fingerprint\n\nconst wire = env.toScannerString();\n\n// Decrypt — requires unlock\nawait aliceKey.unlock('alice-passphrase');\ntry {\n  const decrypted = await MajikEnvelope.fromScannerString(wire).decrypt({\n    fingerprint:    aliceKey.fingerprint,\n    mlKemSecretKey: aliceKey.getMlKemSecretKey()\n  });\n  console.log(decrypted); // 'Direct message.'\n} finally {\n  aliceKey.lock();\n}",
      },
      table: {
        headers: ["Field", "Type", "Size", "Description"],
        rows: [
          {
            cells: ["iv", "string (base64)", "12 bytes", "Random AES-GCM IV"],
          },
          {
            cells: [
              "ciphertext",
              "string (base64)",
              "variable",
              "AES-256-GCM encrypted plaintext",
            ],
          },
          {
            cells: [
              "mlKemCipherText",
              "string (base64)",
              "1088 bytes",
              "ML-KEM-768 encapsulation ciphertext",
            ],
          },
        ],
      },
    },
    {
      id: "group-encryption",
      title: "Group Encryption",
      description:
        "For multi-recipient messages, a single random AES key encrypts the message once. Each recipient gets their own ML-KEM encapsulation of that AES key via a 32-byte XOR one-time-pad.",
      icon: "users",
      content: [
        "A 32-byte random group AES key is generated via `crypto.getRandomValues()`.",
        "The message is encrypted once with that AES key and a fresh 12-byte IV.",
        "For each recipient: `mlKemEncapsulate(recipientPublicKey)` → `sharedSecret`. Then `encryptedAesKey[i] = aesKey[i] XOR sharedSecret[i]` for all 32 bytes.",
        "The payload stores `{ iv, ciphertext, keys: [{ fingerprint, mlKemCipherText, encryptedAesKey }, ...] }` — one `GroupKey` entry per recipient.",
        "The envelope fingerprint header is set to `senderFingerprint` for group messages.",
        "On decryption: find the `GroupKey` entry by fingerprint → decapsulate → XOR to recover `aesKey` → AES-GCM decrypt.",
        "The XOR is safe because `sharedSecret` is 32 uniformly random bytes (ML-KEM output is indistinguishable from random).",
      ],
      codeExample: {
        title: "Group encryption and per-recipient decryption",
        language: "typescript",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// All locked — public keys available without passphrase\nconst [sender, alice, bob] = [senderKey, aliceKey, bobKey].map(MajikKey.fromJSON);\n\nconst env = await MajikEnvelope.encrypt({\n  plaintext: 'Group announcement.',\n  senderFingerprint: sender.fingerprint,\n  recipients: [\n    { fingerprint: alice.fingerprint,  mlKemPublicKey: alice.mlKemPublicKey  },\n    { fingerprint: bob.fingerprint,    mlKemPublicKey: bob.mlKemPublicKey    },\n    { fingerprint: sender.fingerprint, mlKemPublicKey: sender.mlKemPublicKey } // include self\n  ]\n});\n\nconsole.log(env.isGroup); // true\n\n// Bob decrypts independently\nawait bob.unlock('bob-passphrase');\ntry {\n  const text = await env.decrypt({\n    fingerprint:    bob.fingerprint,\n    mlKemSecretKey: bob.getMlKemSecretKey()\n  });\n  console.log(text); // 'Group announcement.'\n} finally {\n  bob.lock();\n}",
      },
      table: {
        headers: ["Field", "Type", "Size", "Description"],
        rows: [
          {
            cells: [
              "iv",
              "string (base64)",
              "12 bytes",
              "Random AES-GCM IV (shared across recipients)",
            ],
          },
          {
            cells: [
              "ciphertext",
              "string (base64)",
              "variable",
              "AES-256-GCM ciphertext (encrypted once)",
            ],
          },
          {
            cells: [
              "keys[n].fingerprint",
              "string (base64)",
              "32 bytes",
              "Recipient identity fingerprint",
            ],
          },
          {
            cells: [
              "keys[n].mlKemCipherText",
              "string (base64)",
              "1088 bytes",
              "ML-KEM-768 ciphertext for this recipient",
            ],
          },
          {
            cells: [
              "keys[n].encryptedAesKey",
              "string (base64)",
              "32 bytes",
              "aesKey XOR mlKemSharedSecret",
            ],
          },
        ],
      },
    },
    {
      id: "majik-key-integration",
      title: "Using @majikah/majik-key as the Identity Source",
      description:
        "MajikEnvelope requires two inputs to encrypt and one to decrypt. Both come directly from a MajikKey instance — public keys are always available, private keys require an explicit unlock() call.",
      icon: "key",
      content: [
        "A MajikKey loaded via MajikKey.fromJSON() is always locked — no private material in memory. This is safe to keep in application state long-term.",
        "Public key material (fingerprint, mlKemPublicKey) is available on a locked key. No passphrase needed. These are the inputs for building a MajikRecipient for encryption.",
        "mlKemPublicKey is a Uint8Array of exactly 1184 bytes — the correct type and size for MajikEnvelope.encrypt() without any conversion.",
        "fingerprint is the base64-encoded SHA-256 of the X25519 public key. It doubles as the account ID and is used as the envelope routing identifier.",
        "For decryption, unlock(passphrase) must be called first. This runs Argon2id (64 MB / 3t / 4p) — expect 600–900 ms. getMlKemSecretKey() returns a Uint8Array of 2400 bytes.",
        "Always call lock() immediately after getMlKemSecretKey() — private key bytes should not linger in memory beyond the decrypt() call.",
        "toContact() returns a MajikContact — the contact representation used by @majikah/majik-message. Use this when building recipient lists from a contacts store rather than constructing the object manually.",
      ],
      table: {
        headers: [
          "MajikEnvelope input",
          "MajikKey source",
          "Unlock needed?",
          "Type",
          "Size",
        ],
        rows: [
          {
            cells: [
              "recipients[n].fingerprint",
              "key.fingerprint",
              "No",
              "string (base64)",
              "32 bytes decoded",
            ],
          },
          {
            cells: [
              "recipients[n].mlKemPublicKey",
              "key.mlKemPublicKey",
              "No",
              "Uint8Array",
              "1184 bytes",
            ],
          },
          {
            cells: [
              "identity.fingerprint",
              "key.fingerprint",
              "No",
              "string (base64)",
              "32 bytes decoded",
            ],
          },
          {
            cells: [
              "identity.mlKemSecretKey",
              "key.getMlKemSecretKey()",
              "Yes — unlock() first",
              "Uint8Array",
              "2400 bytes",
            ],
          },
        ],
      },
      codeExample: {
        title: "Complete encrypt → decrypt with MajikKey",
        language: "typescript",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// ── Setup: load keys from your storage ───────────────────────────────────────\nconst myKey    = MajikKey.fromJSON(storedMyJson);    // sender, always locked on load\nconst aliceKey = MajikKey.fromJSON(storedAliceJson); // recipient contact\n\n// ── ENCRYPT (no unlock needed — public keys only) ─────────────────────────────\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Hello Alice.',\n  recipients: [{\n    fingerprint:    aliceKey.fingerprint,     // always available, no unlock\n    mlKemPublicKey: aliceKey.mlKemPublicKey   // always available, no unlock\n  }]\n});\n\nconst wire = envelope.toScannerString();\n\n// ── DECRYPT (Alice's device — requires unlock) ────────────────────────────────\nconst received = MajikEnvelope.fromScannerString(wire);\n\nawait aliceKey.unlock('alice-passphrase');        // Argon2id — 600–900 ms, show a spinner\ntry {\n  const plaintext = await received.decrypt({\n    fingerprint:    aliceKey.fingerprint,\n    mlKemSecretKey: aliceKey.getMlKemSecretKey()  // 2400-byte secret key\n  });\n  console.log(plaintext); // 'Hello Alice.'\n} finally {\n  aliceKey.lock(); // always lock — even if decrypt() throws\n}",
      },
    },
    {
      id: "group-key-integration",
      title: "Building Group Recipient Lists from MajikKey / MajikContact",
      description:
        "Group encryption needs a MajikRecipient for every participant. MajikKey and MajikContact both expose the exact fields MajikEnvelope.encrypt() expects.",
      icon: "users",
      content: [
        "Each participant is represented as { fingerprint, mlKemPublicKey }. Both fields are available on a locked MajikKey or a MajikContact — no unlock required.",
        "Always include yourself (the sender) as a recipient if you need to decrypt your own sent messages later.",
        "senderFingerprint should be the active (sending) account's fingerprint — it is stored in the binary envelope header, not as a key entry.",
        "In the Majikah stack, toContact() on a MajikKey returns a MajikContact. A MajikContact exposes fingerprint and mlKemPublicKey directly — making contact-list-based recipient building straightforward.",
        "For large groups, build the recipients array from your contacts store — mlKemPublicKey is stored in MajikContact JSON as mlKey (base64). Decode it back to Uint8Array before passing to encrypt().",
      ],
      codeExample: {
        title: "Group encryption from contacts + self-include",
        language: "typescript",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// ── Active sender key ─────────────────────────────────────────────────────────\nconst senderKey = MajikKey.fromJSON(storedSenderJson); // locked\n\n// ── Build recipient list from contacts (all locked / public-only access) ──────\n// Option A: directly from MajikKey instances\nconst recipientKeys = [aliceKey, bobKey, carolKey]; // MajikKey instances\n\nconst recipients = [\n  // Include all participants\n  ...recipientKeys.map(k => ({\n    fingerprint:    k.fingerprint,\n    mlKemPublicKey: k.mlKemPublicKey\n  })),\n  // Always include yourself — needed to decrypt your own sent message\n  {\n    fingerprint:    senderKey.fingerprint,\n    mlKemPublicKey: senderKey.mlKemPublicKey\n  }\n];\n\n// ── Encrypt ───────────────────────────────────────────────────────────────────\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Group announcement for the channel.',\n  senderFingerprint: senderKey.fingerprint, // required for group (2+ recipients)\n  recipients\n});\n\nconsole.log(envelope.isGroup);                           // true\nconsole.log(envelope.toJSON().payload.keys.length);      // 4 (3 recipients + sender)\n\n// ── Any participant decrypts independently ─────────────────────────────────────\nawait bobKey.unlock('bob-passphrase');\ntry {\n  const text = await envelope.decrypt({\n    fingerprint:    bobKey.fingerprint,\n    mlKemSecretKey: bobKey.getMlKemSecretKey()\n  });\n  console.log(text); // 'Group announcement for the channel.'\n} finally {\n  bobKey.lock();\n}",
      },
    },
    {
      id: "wire-format",
      title: "Binary Wire Format & Scanner String",
      description:
        "Envelopes have a deterministic binary layout. The scanner string prefix makes them detectable in any text stream without parsing the payload.",
      icon: "binary",
      content: [
        "Binary layout: `[1 byte: version=3][32 bytes: fingerprint][N bytes: JSON payload]`.",
        "The version byte is always `3` for ML-KEM envelopes. Parsing any other version throws a `MajikEnvelopeError`.",
        "The 32-byte fingerprint is the raw base64-decoded SHA-256 of the recipient's X25519 public key (single) or the sender's fingerprint (group).",
        "The JSON payload is the UTF-8 encoded `EnvelopePayload` object — either `SinglePayload` or `GroupPayload`.",
        "Scanner string format: `~*$MJKMSG:<base64(binary)>`. The regex `^~\\*\\$MJKMSG:([A-Za-z0-9+/=]+)$` is used for detection.",
        "`MessageEnvelope` (legacy parser) can extract the version byte, fingerprint, and raw payload from any v1/v2/v3 binary blob without decrypting — used by message scanner infrastructure.",
      ],
      table: {
        headers: ["Byte Range", "Length", "Content"],
        rows: [
          {
            cells: ["[0]", "1 byte", "Version byte (always 3 for ML-KEM)"],
          },
          {
            cells: ["[1..32]", "32 bytes", "Fingerprint (raw SHA-256 bytes)"],
          },
          {
            cells: [
              "[33..N]",
              "variable",
              "UTF-8 JSON payload (SinglePayload or GroupPayload)",
            ],
          },
        ],
      },
      codeExample: {
        title: "All serialization formats",
        language: "typescript",
        code: "const env = await MajikEnvelope.encrypt({ plaintext: 'hi', recipients: [r] });\n\n// Scanner string — text transport, QR codes, WebSocket messages\nconst wire = env.toScannerString();\n// '~*$MJKMSG:AwAAAA...'\n\n// Binary — binary transports, file storage\nconst bin = env.toBinary(); // ArrayBuffer\n\n// JSON — databases, IndexedDB, envelope caches\nconst json = env.toJSON();\n// { version: 3, fingerprint: '...', payload: {...}, plaintext?: '...' }\n\n// Parse back from any format\nconst fromWire   = MajikEnvelope.fromScannerString(wire);\nconst fromBinary = MajikEnvelope.fromBinary(bin);\nconst fromJson   = MajikEnvelope.fromJSON(json);",
      },
    },
    {
      id: "transparent-compression",
      title: "Transparent Compression",
      description:
        "Plaintext is compressed before encryption and decompressed after decryption — automatically, with no API changes required.",
      icon: "compress",
      content: [
        "Compression is enabled by default (`compress: true` in `EncryptOptions`). Pass `compress: false` to disable.",
        "Plaintext strings use gzip via `fflate` (`gzipSync`) — pure JS, no WASM, synchronous, works everywhere.",
        "Binary payloads use Zstd via `@bokuweb/zstd-wasm` for better compression ratios on structured data.",
        "Compressed payloads are prefixed with `mjkcmp:<type>:<base64>`. The decoder (`MajikCompressor.decompress()`) checks for this prefix automatically.",
        "On decryption, if the decoded plaintext starts with `mjkcmp:`, decompression is applied transparently. Non-compressed payloads pass through unchanged.",
        "This means senders and receivers don't need to coordinate compression — a compressed envelope will always be decompressed correctly on the other end.",
      ],
      codeExample: {
        title: "Compression control",
        language: "typescript",
        code: "// Default: compression on\nconst compressed = await MajikEnvelope.encrypt({\n  plaintext: 'A long message that benefits from compression...'.repeat(100),\n  recipients: [r],\n  compress: true  // default\n});\n\n// Opt out if the payload is already compressed or very short\nconst raw = await MajikEnvelope.encrypt({\n  plaintext: 'Hi',\n  recipients: [r],\n  compress: false\n});\n\n// Decryption is always transparent — no flag needed\nconst text = await compressed.decrypt(identity); // decompressed automatically",
      },
    },
    {
      id: "ml-kem-security",
      title: "ML-KEM-768 Security Model",
      description:
        "Understanding the post-quantum security guarantees and the specific properties of ML-KEM that affect the envelope design.",
      icon: "shield",
      content: [
        "ML-KEM-768 (formerly Kyber-768) is standardized by NIST as FIPS 203. It provides IND-CCA2 security under the Module-LWE hardness assumption, which is believed to be resistant to quantum attacks.",
        "ML-KEM encapsulation is ephemeral per-message — a fresh `cipherText` is generated for every `encrypt()` call, even for the same recipient. There is no reuse of shared secrets across messages.",
        "ML-KEM decapsulation NEVER throws on a wrong key. Instead, it returns a deterministic but useless shared secret — a property called implicit rejection. AES-GCM authentication (the 16-byte tag) catches this: decryption fails with an authentication error, surfaced as a `MajikEnvelopeError`.",
        "For group envelopes, the XOR key wrapping (`encryptedAesKey = aesKey XOR sharedSecret`) is a 32-byte one-time-pad. This is secure because the ML-KEM shared secret is computationally indistinguishable from a random 32-byte string.",
        "The fingerprint in the binary header and group key entries is the SHA-256 hash of the recipient's X25519 public key — it is a public identifier, not secret material. Knowing a fingerprint does not help an attacker decrypt the message.",
      ],
      list: [
        {
          text: "ML-KEM-768, NIST FIPS-203 (Module-LWE lattice-based KEM)",
          label: "Algorithm",
        },
        {
          text: "NIST Level 3 — roughly equivalent to AES-192 classical security",
          label: "Security Level",
        },
        {
          text: "32-byte shared secret + 1088-byte ciphertext",
          label: "Encapsulation Output",
        },
        {
          text: "AES-256-GCM (256-bit key, 128-bit authentication tag)",
          label: "Symmetric Cipher",
        },
        {
          text: "12 bytes, randomly generated per message",
          label: "IV",
        },
        {
          text: "Wrong-key decapsulation returns pseudo-random garbage, caught by AES-GCM auth tag",
          label: "Implicit Rejection",
        },
      ],
    },
    {
      id: "legacy-scanner",
      title: "MessageEnvelope — Legacy Scanner",
      description:
        "A thin binary parser kept for backward compatibility with existing scanner infrastructure. Does not encrypt or decrypt — only extracts structural fields from the binary blob.",
      icon: "scan",
      content: [
        "`MessageEnvelope` wraps a raw binary blob. It can extract the version byte, fingerprint, and raw JSON payload without performing any cryptographic operations.",
        "`MessageEnvelope.isEnvelopeCandidate(input)` quickly checks if a string matches the `~*$MJKMSG:<base64>` pattern — useful in message stream filters before any parsing.",
        "`MessageEnvelope.tryFromString(raw)` returns `{ envelope?, error? }` — a non-throwing parser for use in contexts where invalid input is expected.",
        "`getVersion()` returns `1`, `2`, or `3`. Use this to route v3 envelopes to `MajikEnvelope.fromBinary()` and handle or reject older versions.",
        "`getRecipientKey(fingerprint)` extracts the `GroupKey` entry for a given fingerprint from the raw payload — useful for pre-filtering before decryption.",
        "New code should use `MajikEnvelope` directly. `MessageEnvelope` is kept for scanner infrastructure that needs to inspect envelopes without decrypting them.",
      ],
      codeExample: {
        title: "Using MessageEnvelope for pre-filtering",
        language: "typescript",
        code: "import { MessageEnvelope } from '@majikah/majik-envelope';\n\n// Quick candidate check before parsing\nif (!MessageEnvelope.isEnvelopeCandidate(rawString)) {\n  return; // not an envelope, skip\n}\n\n// Non-throwing parse\nconst { envelope, error } = MessageEnvelope.tryFromString(rawString);\nif (error || !envelope) return;\n\n// Check version before routing to decryption\nconst version = envelope.getVersion();\nif (version !== 3) {\n  console.warn('Unsupported envelope version:', version);\n  return;\n}\n\n// Check if this envelope is for me (by fingerprint)\nif (!envelope.hasRecipient(myFingerprint)) return;\n\n// Now parse and decrypt with MajikEnvelope\nconst mjkEnv = MajikEnvelope.fromBinary(envelope.raw);\nconst plaintext = await mjkEnv.decrypt(myIdentity);",
      },
    },
  ],
  useCases: [
    {
      title: "Direct Messaging (1:1)",
      description:
        "Encrypt a message for a single recipient. The ML-KEM shared secret is used directly as the AES key — minimal overhead, maximum simplicity.",
      scenario:
        "Alice wants to send Bob an encrypted message. She has Bob's ML-KEM-768 public key from his MajikContact. She encrypts, transmits the scanner string, and Bob decrypts with his secret key after unlocking his MajikKey.",
      benefits: [
        "No shared secret pre-establishment — ML-KEM encapsulation is the key exchange",
        "One function call to encrypt, one to decrypt",
        "Scanner string works over any text channel: WebSocket, HTTP, QR code, copy-paste",
      ],
      icon: "message",
    },
    {
      title: "Group Messaging",
      description:
        "Encrypt one message for N recipients. The message is encrypted once; each recipient gets an independent key entry. Any recipient can decrypt without knowing about the others.",
      scenario:
        "A group chat has 5 participants. The sender encrypts with all 5 ML-KEM public keys. The relay stores one envelope. Each participant decrypts independently using their own secret key — the relay never sees the plaintext.",
      benefits: [
        "O(1) ciphertext size — message encrypted once regardless of recipient count",
        "O(N) key entries — scales linearly with group size",
        "Each recipient's decryption is fully independent — no coordination needed",
      ],
      codeExample: {
        title: "Group message to 5 participants",
        language: "typescript",
        code: "const participants = [alice, bob, carol, dave, eve];\n\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: messageText,\n  senderFingerprint: sender.fingerprint,\n  recipients: participants.map(p => ({\n    fingerprint: p.fingerprint,\n    mlKemPublicKey: p.mlKemPublicKey\n  }))\n});\n\n// Store or transmit the single scanner string\nawait relay.store(conversationId, envelope.toScannerString());\n\n// Each participant decrypts with their own key\nconst myText = await envelope.decrypt({\n  fingerprint: alice.fingerprint,\n  mlKemSecretKey: alice.getMlKemSecretKey()\n});",
      },
      icon: "users",
    },
    {
      title: "Message Stream Scanning",
      description:
        "Use MessageEnvelope to scan a raw message stream for envelope candidates and pre-filter by recipient fingerprint before attempting decryption.",
      scenario:
        "A relay or local message store receives raw strings of mixed types. A scanner checks each string for the `~*$MJKMSG:` prefix, extracts the fingerprint, checks if it matches the active account, and routes matching envelopes to the decryption queue.",
      benefits: [
        "isEnvelopeCandidate() is a pure regex check — zero crypto overhead for non-matches",
        "hasRecipient() extracts the group key lookup without decrypting",
        "tryFromString() never throws — safe for untrusted or malformed input",
      ],
      icon: "scan",
    },
    {
      title: "Offline Encrypted Storage",
      description:
        "Serialize envelopes to JSON and store them in IndexedDB, Supabase, or any database. The plaintext field can be cached post-decryption for local display without re-decrypting.",
      scenario:
        "An Electron app stores received messages as MajikEnvelopeJSON objects. On load, if plaintext is populated in the JSON (cached after first decryption), the message is displayed immediately. If not, it decrypts and updates the cache.",
      benefits: [
        "toJSON() / fromJSON() round-trips losslessly",
        "plaintext in JSON is optional — populate it after first decryption as a cache",
        "Version field in JSON allows future format migrations",
      ],
      codeExample: {
        title: "Store and restore from JSON",
        language: "typescript",
        code: "// After receiving and decrypting\nconst plaintext = await envelope.decrypt(identity);\nconst stored = envelope.toJSON(); // plaintext now populated in JSON\nawait db.messages.put(stored);\n\n// Later: restore\nconst loaded = MajikEnvelope.fromJSON(await db.messages.get(id));\nif (loaded.plaintext) {\n  display(loaded.plaintext); // no decryption needed\n} else {\n  display(await loaded.decrypt(identity)); // decrypt and cache\n}",
      },
      icon: "database",
    },
  ],
  examples: [
    {
      title: "Single Recipient Round-Trip",
      description:
        "Complete encrypt → serialize → parse → decrypt cycle for a 1:1 message.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Single recipient — full cycle",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// Load Alice's key from storage — always locked on load\nconst aliceKey = MajikKey.fromJSON(storedAliceJson);\n\n// Public fields — no unlock needed\nconst recipient = {\n  fingerprint:    aliceKey.fingerprint,\n  mlKemPublicKey: aliceKey.mlKemPublicKey  // Uint8Array, 1184 bytes\n};\n\n// Encrypt\nconst env = await MajikEnvelope.encrypt({\n  plaintext: 'Hello Alice.',\n  recipients: [recipient]\n});\n\nconsole.log(env.version);    // 3\nconsole.log(env.isSingle);   // true\nconsole.log(env.fingerprint); // aliceKey.fingerprint\n\n// Serialize to scanner string\nconst wire = env.toScannerString();\nconsole.log(wire.startsWith('~*$MJKMSG:')); // true\n\n// ── Decrypt (Alice's device) ──────────────────────────────────────────────────\nconst received = MajikEnvelope.fromScannerString(wire);\n\n// unlock() runs Argon2id — show a loading indicator in your UI\nawait aliceKey.unlock('alice-passphrase');\ntry {\n  const plaintext = await received.decrypt({\n    fingerprint:    aliceKey.fingerprint,\n    mlKemSecretKey: aliceKey.getMlKemSecretKey() // Uint8Array, 2400 bytes\n  });\n  console.log(plaintext); // 'Hello Alice.'\n} finally {\n  aliceKey.lock(); // always lock in finally\n}",
        },
      ],
    },
    {
      title: "Group Encryption",
      description:
        "Encrypt for multiple recipients, then decrypt as each participant.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "3-participant group message",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// All three locked — public key access requires no passphrase\nconst sender = MajikKey.fromJSON(storedSenderJson);\nconst alice  = MajikKey.fromJSON(storedAliceJson);\nconst bob    = MajikKey.fromJSON(storedBobJson);\n\nconst env = await MajikEnvelope.encrypt({\n  plaintext: 'Group message.',\n  senderFingerprint: sender.fingerprint,\n  recipients: [\n    { fingerprint: alice.fingerprint,  mlKemPublicKey: alice.mlKemPublicKey  },\n    { fingerprint: bob.fingerprint,    mlKemPublicKey: bob.mlKemPublicKey    },\n    { fingerprint: sender.fingerprint, mlKemPublicKey: sender.mlKemPublicKey } // include self\n  ]\n});\n\nconsole.log(env.isGroup); // true\n\n// Decrypt as Bob\nawait bob.unlock('bob-passphrase');\ntry {\n  const text = await env.decrypt({\n    fingerprint:    bob.fingerprint,\n    mlKemSecretKey: bob.getMlKemSecretKey()\n  });\n  console.log(text); // 'Group message.'\n} finally {\n  bob.lock();\n}",
        },
      ],
    },
    {
      title: "MajikKey Integration",
      description:
        "Full workflow: create or load a MajikKey, extract the inputs MajikEnvelope expects, encrypt, and decrypt.",
      category: "Integration",
      difficulty: "beginner",
      examples: [
        {
          title: "New identity → encrypt → decrypt",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// ── Create fresh identities (one-time setup) ──────────────────────────────────\nconst aliceMnemonic = MajikKey.generateMnemonic();\nconst bobMnemonic   = MajikKey.generateMnemonic();\n\nconst aliceKey = await MajikKey.create(aliceMnemonic, 'alice-pass', 'Alice Phone');\nconst bobKey   = await MajikKey.create(bobMnemonic,   'bob-pass',   'Bob Laptop');\n// Both start unlocked after create()\n\n// Persist both (no plaintext keys in storage — only encrypted blobs)\nconst storedAlice = aliceKey.toJSON();\nconst storedBob   = bobKey.toJSON();\naliceKey.lock();\nbobKey.lock();\n\n// ── Later: Alice encrypts a message for Bob ───────────────────────────────────\nconst bobLoaded = MajikKey.fromJSON(storedBob); // locked, public keys accessible\n\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Hey Bob, this is quantum-safe.',\n  recipients: [{\n    fingerprint:    bobLoaded.fingerprint,      // no unlock\n    mlKemPublicKey: bobLoaded.mlKemPublicKey    // no unlock\n  }]\n});\n\nconst wire = envelope.toScannerString();\n\n// ── Bob decrypts ──────────────────────────────────────────────────────────────\nconst bobDecrypt = MajikKey.fromJSON(storedBob);\nawait bobDecrypt.unlock('bob-pass'); // Argon2id — show a spinner\ntry {\n  const plaintext = await MajikEnvelope.fromScannerString(wire).decrypt({\n    fingerprint:    bobDecrypt.fingerprint,\n    mlKemSecretKey: bobDecrypt.getMlKemSecretKey()\n  });\n  console.log(plaintext); // 'Hey Bob, this is quantum-safe.'\n} finally {\n  bobDecrypt.lock();\n}",
        },
        {
          title: "Load existing key from JSON → encrypt → decrypt",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikEnvelope } from '@majikah/majik-envelope';\n\n// ── Encryption side: public keys only ────────────────────────────────────────\n// fromJSON() always returns a locked key\nconst recipientKey = MajikKey.fromJSON(storedRecipientJson);\n\nconsole.log(recipientKey.isLocked);        // true\nconsole.log(recipientKey.hasMlKem);        // true — ML-KEM keys present\nconsole.log(recipientKey.isFullyUpgraded); // true — Argon2id + ML-KEM-768\n\n// fingerprint and mlKemPublicKey are read without any unlock\nconst envelope = await MajikEnvelope.encrypt({\n  plaintext: 'Encrypted payload.',\n  recipients: [{\n    fingerprint:    recipientKey.fingerprint,\n    mlKemPublicKey: recipientKey.mlKemPublicKey\n  }]\n});\n\n// ── Decryption side: private key needed ──────────────────────────────────────\nconst decryptKey = MajikKey.fromJSON(storedRecipientJson);\n\n// Verify passphrase before unlock (optional — avoids unnecessary Argon2id run)\nconst isValid = await decryptKey.verify('user-passphrase');\nif (!isValid) throw new Error('Wrong passphrase');\n\nawait decryptKey.unlock('user-passphrase');\ntry {\n  const plaintext = await envelope.decrypt({\n    fingerprint:    decryptKey.fingerprint,\n    mlKemSecretKey: decryptKey.getMlKemSecretKey()\n  });\n  console.log(plaintext); // 'Encrypted payload.'\n} finally {\n  decryptKey.lock();\n}",
        },
      ],
    },
    {
      title: "All Serialization Formats",
      description:
        "Converting an envelope between scanner string, binary, and JSON.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Three serialization formats",
          language: "typescript",
          code: "const env = await MajikEnvelope.encrypt({ plaintext: 'test', recipients: [r] });\n\n// 1. Scanner string (text transport)\nconst wire = env.toScannerString();\nconst fromWire = MajikEnvelope.fromScannerString(wire);\n\n// 2. Binary (binary transport or file)\nconst bin = env.toBinary();\nconst fromBin = MajikEnvelope.fromBinary(bin);\n\n// 3. JSON (database storage)\nconst json = env.toJSON();\n// { version: 3, fingerprint, payload: { iv, ciphertext, mlKemCipherText } }\nconst fromJson = MajikEnvelope.fromJSON(json);\n\n// All three parse to equivalent envelopes\nconsole.log(fromWire.fingerprint === env.fingerprint);   // true\nconsole.log(fromBin.fingerprint  === env.fingerprint);   // true\nconsole.log(fromJson.fingerprint === env.fingerprint);   // true",
        },
      ],
    },
    {
      title: "MessageEnvelope Scanner",
      description:
        "Using the legacy parser to scan a mixed message stream without decrypting.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Pre-filter before decryption",
          language: "typescript",
          code: "import { MessageEnvelope, MajikEnvelope } from '@majikah/majik-envelope';\n\nasync function processMessage(raw: string, identity: MajikIdentity) {\n  // 1. Quick candidate check (regex only)\n  if (!MessageEnvelope.isEnvelopeCandidate(raw)) return null;\n\n  // 2. Non-throwing parse\n  const { envelope, error } = MessageEnvelope.tryFromString(raw);\n  if (error || !envelope) return null;\n\n  // 3. Version gate\n  if (envelope.getVersion() !== 3) {\n    console.warn('Dropping non-v3 envelope');\n    return null;\n  }\n\n  // 4. Recipient check (no crypto)\n  if (!envelope.hasRecipient(identity.fingerprint)) return null;\n\n  // 5. Full parse + decrypt\n  const mjkEnv = MajikEnvelope.fromBinary(envelope.raw);\n  return mjkEnv.decrypt(identity);\n}",
        },
      ],
    },
    {
      title: "Compression Control",
      description:
        "Controlling compression behavior and understanding the compressed payload prefix.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Compress on/off and payload inspection",
          language: "typescript",
          code: "// Large payload — compression helps\nconst bigEnv = await MajikEnvelope.encrypt({\n  plaintext: JSON.stringify(largeObject),\n  recipients: [r],\n  compress: true // default\n});\n\n// Small payload or already-compressed data — skip compression overhead\nconst rawEnv = await MajikEnvelope.encrypt({\n  plaintext: 'Hi',\n  recipients: [r],\n  compress: false\n});\n\n// After decryption, if compressed the payload internally looked like:\n// 'mjkcmp:str:<base64 gzip>' — decompressed transparently by decrypt()",
        },
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Primitives",
        table: {
          headers: ["Primitive", "Library", "Version", "Usage"],
          rows: [
            {
              cells: [
                "ML-KEM-768 (Kyber)",
                "@noble/post-quantum",
                "^0.5.4",
                "Key encapsulation — NIST FIPS-203, Level 3",
              ],
            },
            {
              cells: [
                "AES-256-GCM",
                "@stablelib/aes + @stablelib/gcm",
                "^2.0.1",
                "Symmetric message encryption, 256-bit key, 128-bit auth tag",
              ],
            },
            {
              cells: [
                "SHA-256",
                "@stablelib/sha256",
                "^2.0.1",
                "Fingerprint hashing (X25519 public key → 32-byte identifier)",
              ],
            },
            {
              cells: [
                "PBKDF2-SHA256",
                "@stablelib/pbkdf2",
                "^2.0.1",
                "Legacy KDF v1 (read-only, not used for envelope encryption)",
              ],
            },
            {
              cells: [
                "Gzip",
                "fflate",
                "^0.8.2",
                "Plaintext string compression (synchronous, no WASM)",
              ],
            },
            {
              cells: [
                "Zstd",
                "@bokuweb/zstd-wasm",
                "^0.0.27",
                "Binary payload compression (WASM, higher ratio)",
              ],
            },
          ],
        },
      },
      {
        title: "Key & Payload Sizes",
        table: {
          headers: ["Element", "Size", "Notes"],
          rows: [
            {
              cells: [
                "ML-KEM-768 public key",
                "1184 bytes",
                "Recipient's mlKemPublicKey input",
              ],
            },
            {
              cells: [
                "ML-KEM-768 secret key",
                "2400 bytes",
                "Decrypting identity's mlKemSecretKey input",
              ],
            },
            {
              cells: [
                "ML-KEM-768 ciphertext (mlKemCipherText)",
                "1088 bytes",
                "Stored in SinglePayload and per GroupKey entry",
              ],
            },
            {
              cells: [
                "ML-KEM shared secret",
                "32 bytes",
                "Ephemeral — used as AES key or XOR pad, never stored",
              ],
            },
            {
              cells: [
                "AES-256-GCM IV",
                "12 bytes",
                "Randomly generated per message",
              ],
            },
            {
              cells: [
                "AES-256-GCM authentication tag",
                "16 bytes",
                "Appended to ciphertext by @stablelib/gcm",
              ],
            },
            {
              cells: [
                "Group AES key",
                "32 bytes",
                "Randomly generated per group message, never stored in plaintext",
              ],
            },
            {
              cells: [
                "encryptedAesKey (per recipient)",
                "32 bytes",
                "aesKey XOR mlKemSharedSecret",
              ],
            },
            {
              cells: [
                "Fingerprint (binary header)",
                "32 bytes",
                "Raw SHA-256 bytes (not base64 in binary format)",
              ],
            },
          ],
        },
      },
      {
        title: "Binary Format Layout",
        table: {
          headers: ["Offset", "Length", "Field", "Value"],
          rows: [
            {
              cells: [
                "0",
                "1 byte",
                "version",
                "Always 3 for ML-KEM envelopes",
              ],
            },
            {
              cells: [
                "1",
                "32 bytes",
                "fingerprint",
                "Raw SHA-256 of recipient's X25519 pubkey (single) or sender's fingerprint (group)",
              ],
            },
            {
              cells: [
                "33",
                "variable",
                "payload",
                "UTF-8 JSON: SinglePayload or GroupPayload",
              ],
            },
          ],
        },
      },
      {
        title: "Compression Format",
        description:
          "Compressed payloads use a tagged prefix string: `mjkcmp:<type>:<base64-compressed-data>`. The `<type>` field is one of `str` (string), `json` (object), or `blob` (Uint8Array), used to restore the correct output type on decompression.",
        list: [
          "Prefix: mjkcmp:<type>:<base64>",
          "Plaintext mode (strings): gzip via fflate — synchronous, no WASM, ~40-70% reduction on repetitive text",
          "Binary mode (objects/buffers): Zstd via WASM — async init on first use, higher ratio for structured data",
          "Detection: decrypt() checks if decoded plaintext starts with 'mjkcmp:' before decompressing",
          "Non-compressed payloads pass through unchanged — no flag or coordination needed between sender and receiver",
        ],
      },
    ],
  },
  compatibility: {
    title: "Compatibility",
    description:
      "@majikah/majik-envelope is distributed as an ES module (type: module). It requires environments that support SubtleCrypto, Uint8Array, and TextEncoder.",
    runtime: [
      {
        name: "Node.js",
        supported: true,
        minVersion: "18",
        notes:
          "SubtleCrypto available via globalThis.crypto. Zstd WASM (binary compression) requires a WASM-capable runtime — available in Node 18+.",
      },
      {
        name: "Browser",
        supported: true,
        notes:
          "Full support on all modern browsers with HTTPS (SubtleCrypto requires a secure context). Zstd WASM loaded on first binary compress/decompress call.",
      },
      {
        name: "Electron",
        supported: true,
        notes:
          "Works in both main and renderer processes. WASM available natively.",
      },
      {
        name: "Cloudflare Workers",
        supported: true,
        notes:
          "SubtleCrypto available. Zstd WASM may require explicit WASM binding configuration. Gzip (fflate) plaintext compression works without WASM.",
      },
      {
        name: "Bun",
        supported: true,
        notes: "Full compatibility. SubtleCrypto and WASM supported natively.",
      },
      {
        name: "Deno",
        supported: true,
        notes:
          "Requires --allow-net and --allow-read for WASM loading. Gzip compression works without WASM flags.",
      },
    ],
    typescript: {
      included: true,
      strictMode: true,
      declaration: true,
    },
  },
  bestPractices: {
    title: "Best Practices",
    practices: [
      {
        title: "Unlock only to decrypt — lock in a finally block",
        description:
          "MajikKey private keys are cleared from memory on lock(). The unlock window should be as short as possible. Always lock in a finally block so private bytes are cleared even when decrypt() throws.",
        dos: [
          "Call unlock() immediately before decrypt() and lock() immediately after in a finally block",
          "Use try/finally — not try/catch — so lock() runs even when decrypt() throws a MajikEnvelopeError",
          "Show a loading indicator during unlock() — Argon2id takes 600–900 ms by design",
        ],
        donts: [
          "Don't keep a MajikKey unlocked across multiple messages — unlock per-operation",
          "Don't store getMlKemSecretKey() result in a variable beyond the decrypt() call",
          "Don't catch errors from decrypt() before calling lock() — use finally",
        ],
        category: "Security",
      },
      {
        title: "Always include yourself as a recipient in group messages",
        description:
          "If you need to decrypt your own sent messages later (e.g. for a sent-messages view), include your own ML-KEM public key in the recipients array. There is no separate 'sender decryption' mechanism.",
        dos: [
          "Add your own { fingerprint, mlKemPublicKey } to the recipients array in group encryptions",
          "Use senderFingerprint as your fingerprint so the envelope header identifies you as the sender",
        ],
        donts: [
          "Don't assume you can decrypt a message you sent — you need your own key entry in the payload",
          "Don't store plaintext server-side as a workaround for missing self-decryption",
        ],
        category: "Security",
      },
      {
        title: "Never reuse envelopes — encrypt fresh for every send",
        description:
          "ML-KEM encapsulation generates ephemeral shared secrets. Each encrypt() call produces a fresh ciphertext with a new random IV and new ML-KEM key encapsulations. Never replay or reuse a toScannerString() result.",
        dos: [
          "Call MajikEnvelope.encrypt() for every new message send",
          "Treat the scanner string as a one-time artifact — transmit once, then discard or archive",
        ],
        donts: [
          "Don't cache and resend the same scanner string for repeated deliveries",
          "Don't derive a long-lived AES key from an ML-KEM shared secret outside the envelope",
        ],
        category: "Security",
      },
      {
        title: "Validate recipient key sizes before encrypting",
        description:
          "encrypt() validates that every mlKemPublicKey is exactly 1184 bytes and throws a MajikEnvelopeError before encryption if invalid. Use MajikKey.mlKemPublicKey directly — it is always the correct Uint8Array by construction. Only validate manually when building recipient lists from raw external data.",
        dos: [
          "Use key.mlKemPublicKey directly from a MajikKey instance — always a correctly-sized Uint8Array",
          "Check mlKemPublicKey.length === 1184 when loading keys from external or untrusted sources",
          "Check key.hasMlKem === true and key.isFullyUpgraded === true before using a key for envelope encryption",
        ],
        donts: [
          "Don't pass a base64 string as mlKemPublicKey — it must be a decoded Uint8Array",
          "Don't use keys where key.hasMlKem is false — they are legacy accounts that need migration via importFromMnemonicBackup()",
          "Don't skip key.isFullyUpgraded checks in relay or server-side code that constructs recipient lists",
        ],
        category: "Security",
      },
      {
        title:
          "Use MessageEnvelope for stream scanning, MajikEnvelope for everything else",
        description:
          "MessageEnvelope is a lightweight structural parser — use it to check if a string is an envelope and whether it's addressed to the current user before invoking the heavier MajikEnvelope parsing and decryption path.",
        dos: [
          "Use MessageEnvelope.isEnvelopeCandidate() as the first filter in message processing pipelines",
          "Use envelope.hasRecipient(fingerprint) to skip decryption for envelopes not addressed to the active account",
        ],
        donts: [
          "Don't attempt to decrypt envelopes from MessageEnvelope — it has no decryption capability",
          "Don't use MajikEnvelope.fromScannerString() on untrusted bulk input without the isEnvelopeCandidate() pre-check",
        ],
        category: "Performance",
      },
      {
        title: "Cache decrypted plaintext in toJSON() after first decrypt",
        description:
          "toJSON() includes an optional plaintext field. After the first successful decrypt(), the plaintext is set on the instance and will be included in subsequent toJSON() calls. Use this to avoid re-decrypting on every render.",
        dos: [
          "Call decrypt() once and immediately persist the updated toJSON() to your store",
          "Check loaded.plaintext before calling decrypt() — skip decryption if already populated",
        ],
        donts: [
          "Don't decrypt on every render cycle — decrypt once and cache",
          "Don't store plaintext in a separate column — keep it co-located with the envelope JSON for atomicity",
        ],
        category: "Performance",
      },
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    commonIssues: [
      {
        problem:
          "decrypt() throws 'Decryption failed — wrong key or corrupted envelope'",
        symptoms: [
          "MajikEnvelopeError thrown from decrypt()",
          "envelope.plaintext remains undefined after the call",
        ],
        causes: [
          "The wrong ML-KEM secret key was provided — fingerprint mismatch or wrong identity",
          "The envelope was created for a different recipient",
          "The binary blob was corrupted or truncated during transport",
          "For group envelopes: no GroupKey entry matches the provided fingerprint",
        ],
        solutions: [
          "Use await key.verify(passphrase) before unlock() to confirm the passphrase is correct without leaving the key unlocked",
          "Verify identity.fingerprint matches: for single envelopes check envelope.fingerprint; for group envelopes check envelope.toJSON().payload.keys[].fingerprint",
          "Ensure key.hasMlKem === true — keys where hasMlKem is false cannot produce a valid getMlKemSecretKey()",
          "Confirm mlKemSecretKey.length === 2400 — a truncated or wrongly-decoded key always fails",
          "Ensure the MajikKey was fully upgraded: key.isFullyUpgraded should be true. If false, run importFromMnemonicBackup() from @majikah/majik-key first",
        ],
      },
      {
        problem:
          "encrypt() throws 'senderFingerprint is required for group messages'",
        symptoms: ["MajikEnvelopeError thrown when recipients.length > 1"],
        causes: [
          "senderFingerprint was omitted from EncryptOptions when passing multiple recipients",
        ],
        solutions: [
          "Always include senderFingerprint when encrypting for 2 or more recipients",
          "Use the active account's fingerprint: senderFingerprint: myKey.fingerprint",
        ],
      },
      {
        problem: "fromScannerString() throws 'Invalid format'",
        symptoms: [
          "MajikEnvelopeError with message 'Invalid format — expected ~*$MJKMSG:<base64>'",
        ],
        causes: [
          "The input string does not match the ~*$MJKMSG:<base64> pattern",
          "The string was URL-encoded or HTML-escaped during transport (+ → %2B, = → %3D, etc.)",
          "The string was truncated",
        ],
        solutions: [
          "Use MessageEnvelope.isEnvelopeCandidate(str) to pre-validate before parsing",
          "Decode URL encoding before passing to fromScannerString()",
          "Ensure the full string including the ~*$MJKMSG: prefix is transmitted without modification",
        ],
      },
      {
        problem:
          "fromBinary() throws 'Unsupported envelope version: 1' or 'version: 2'",
        symptoms: ["MajikEnvelopeError with unsupported version number"],
        causes: [
          "The binary blob is a v1 (X25519 solo) or v2 (X25519 group) envelope — legacy formats not supported by MajikEnvelope",
        ],
        solutions: [
          "Use MessageEnvelope.getVersion() to check version before routing to MajikEnvelope",
          "v1/v2 envelopes require the legacy decryption path — MajikEnvelope only handles v3",
          "Re-encrypt the content with MajikEnvelope.encrypt() to produce a v3 envelope",
        ],
      },
      {
        problem: "Zstd decompression fails or WASM not initialized",
        symptoms: [
          "Error from MajikCompressor during decrypt() on binary-mode compressed payloads",
          "Error: WASM not initialized",
        ],
        causes: [
          "Binary mode compression (Zstd) requires WASM to be initialized before first use",
          "In Cloudflare Workers or restricted environments, WASM loading may fail",
        ],
        solutions: [
          "Most envelopes use plaintext mode (gzip via fflate) — this requires no WASM and will not fail",
          "For binary mode, ensure @bokuweb/zstd-wasm is bundled correctly in your build",
          "In Cloudflare Workers, configure WASM bindings explicitly or disable binary-mode compression",
        ],
      },
    ],
    debugging: [
      {
        title: "Inspect envelope structure without decrypting",
        description:
          "Use getters to understand the envelope before attempting decryption.",
        code: {
          language: "typescript",
          code: "const env = MajikEnvelope.fromScannerString(wire);\n\nconsole.log(env.version);    // 3\nconsole.log(env.fingerprint); // recipient or sender fingerprint\nconsole.log(env.isSingle);   // true for 1:1\nconsole.log(env.isGroup);    // true for multi-recipient\n\n// Inspect the raw JSON to see key entries\nconst json = env.toJSON();\nif (json.payload && 'keys' in json.payload) {\n  console.log('Group recipients:', json.payload.keys.map(k => k.fingerprint));\n}",
        },
      },
      {
        title: "Check if an envelope is addressed to you",
        description:
          "Use MessageEnvelope for a lightweight fingerprint check before full parsing.",
        code: {
          language: "typescript",
          code: "const { envelope } = MessageEnvelope.tryFromString(rawString);\nif (envelope) {\n  const isForMe = envelope.hasRecipient(myFingerprint)\n    || envelope.extractFingerprint() === myFingerprint;\n  console.log('addressed to me:', isForMe);\n}",
        },
      },
    ],
  },
  ecosystem: {
    title: "Ecosystem",
    paragraphs: [
      "@majikah/majik-envelope is the encryption engine layer of the Majikah SDK stack. It sits between the identity layer (@majikah/majik-key) and the messaging layer (@majikah/majik-message), handling all envelope creation, parsing, and decryption.",
      "The library depends on @noble/post-quantum for ML-KEM-768 — one of the most audited pure-JS post-quantum implementations available — and @stablelib for AES-256-GCM and SHA-256.",
    ],
    relatedPackages: [
      {
        name: "@majikah/majik-key",
        description:
          "Identity layer. Generates and manages ML-KEM-768 and X25519 keypairs from BIP-39 mnemonics. Provides the mlKemPublicKey and getMlKemSecretKey() used as inputs to MajikEnvelope.",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-message",
        description:
          "Full messaging SDK. Orchestrates MajikKey + MajikEnvelope into a complete E2EE messaging system with contact management, conversation state, and relay integration.",
        url: "https://www.npmjs.com/package/@majikah/majik-message",
        relationship: "complement",
      },
      {
        name: "@noble/post-quantum",
        description:
          "Audited pure-JS NIST post-quantum algorithms. Provides ml_kem768.encapsulate() and ml_kem768.decapsulate() — the core KEM operations in MajikEnvelope.",
        url: "https://www.npmjs.com/package/@noble/post-quantum",
        relationship: "dependency",
      },
      {
        name: "fflate",
        description:
          "High-performance pure-JS compression library. Used for gzip compression of plaintext strings before encryption — synchronous, no WASM required.",
        url: "https://www.npmjs.com/package/fflate",
        relationship: "dependency",
      },
      {
        name: "@bokuweb/zstd-wasm",
        description:
          "WASM-based Zstd compression. Used for binary payload compression when higher compression ratios are needed.",
        url: "https://www.npmjs.com/package/@bokuweb/zstd-wasm",
        relationship: "dependency",
      },
      {
        name: "@stablelib/aes + @stablelib/gcm",
        description:
          "Pure-JS AES-256-GCM implementation. Handles symmetric message encryption and authentication after ML-KEM key encapsulation.",
        url: "https://www.npmjs.com/package/@stablelib/gcm",
        relationship: "dependency",
      },
    ],
  },
  support: {
    title: "Support & Resources",
    documentation: [
      {
        label: "GitHub Repository",
        url: "https://github.com/Majikah/majik-envelope",
        type: "reference",
      },
      {
        label: "npm Package",
        url: "https://www.npmjs.com/package/@majikah/majik-envelope",
        type: "reference",
      },
    ],
    community: [
      {
        platform: "GitHub Issues",
        url: "https://github.com/Majikah/majik-envelope/issues",
      },
      {
        platform: "GitHub Sponsors",
        url: "https://github.com/sponsors/jedlsf",
      },
    ],
    issues: {
      url: "https://github.com/Majikah/majik-envelope/issues",
    },
    contributing:
      "https://github.com/Majikah/majik-envelope/blob/main/CONTRIBUTING.md",
  },
  cta: {
    title: "Start Building with Majik Envelope",
    description:
      "Post-quantum message encryption in two function calls. Encrypt for one recipient or fifty — the API is identical. No X25519, no legacy paths, no compromises.",
    actions: [
      {
        label: "npm install @majikah/majik-envelope",
        url: "https://www.npmjs.com/package/@majikah/majik-envelope",
        type: "install",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-envelope",
        type: "github",
      },
    ],
    closingStatement:
      "Built by Zelijah for the Majikah ecosystem. Open source under Apache-2.0.",
  },
  version: "0.0.1",
  isEnabled: true,
};
